import { ApolloError, useMutation } from "@apollo/client";
import {
  UpdateCurrentUserProfileDocument,
  type UpdateCurrentUserProfileMutationVariables,
} from "../../gql/generated/graphql";
import { notifications } from "@mantine/notifications";
import { useTranslation } from "react-i18next";

export const useUserProfile = () => {
  const { t } = useTranslation("use-cases");

  const [updateProfile, { loading: isUpdatingProfile, error: updateError }] =
    useMutation(UpdateCurrentUserProfileDocument);

  return {
    isUpdatingProfile,
    updateProfile: async ({
      data,
      onError,
      catchInputErrors = false,
    }: {
      data: UpdateCurrentUserProfileMutationVariables["data"];
      onError?: (error: ApolloError | Error) => void;
      catchInputErrors?: boolean;
    }): Promise<boolean> => {
      try {
        await updateProfile({
          variables: {
            data,
          },
        });

        notifications.show({
          color: "green",
          message: t("hooks.useUserProfile.notifications.updateSuccess"),
        });

        return true;
      } catch (e) {
        if (e instanceof Error || e instanceof ApolloError) {
          onError?.(e);
        }

        if (
          catchInputErrors &&
          e instanceof ApolloError &&
          e.graphQLErrors.some(err => err.extensions?.code !== "BAD_USER_INPUT")
        ) {
          notifications.show({
            color: "red",
            message: t("hooks.useUserProfile.notifications.updateError"),
          });
        } else {
          throw e;
        }

        return false;
      }
    },
    updateError,
  };
};
